import axios from "axios";
import { Endpoints } from "../../config/api";
import { formatYearMonth } from "../actions/actions";

let currentToken = null;
let refreshToken = null;
let requestsToRefresh = [];
let isRefreshing = false;
let remember = false;

// axios instance for calls requiring authentication with JWT token
const authenticatedApiClient = axios.create();

axios.defaults.headers['Content-Type'] = "application/json";
authenticatedApiClient.defaults.headers['Content-Type'] = "application/json";


const setTokens = (token, refresh, rememberMe) => {
    authenticatedApiClient.defaults.headers['Authorization'] = 'Bearer ' + token;
    axios.defaults.headers['Authorization'] = 'Bearer ' + token;
    currentToken = token;
    refreshToken = refresh;
    remember = rememberMe;
    if(rememberMe){
        localStorage.setItem("token", currentToken);
        localStorage.setItem("refreshToken", refreshToken);
    };
};

export const clearTokens = () => {
    console.log("Clearing tokens");
    authenticatedApiClient.defaults.headers['Authorization'] = null;
    axios.defaults.headers['Authorization'] = null;
    currentToken = null;
    refreshToken = null;
    localStorage.clear();
}

authenticatedApiClient.interceptors.response.use(null, (err) => {
    const { response, config } = err;
    if(response){
        if (response.status === 401) {
            // If we have not logged in before, it makes no sense
            // to try to get a new token
            if (!currentToken) {
              return Promise.reject(err)
            };
    
            if (!isRefreshing) {
                isRefreshing = true
                console.log('Request for a new token')
                // Send request to refresh token
                axios({
                    method: "GET",
                    url: Endpoints.REFRESH,
                    headers: {
                        "Authorization": 'Bearer ' + refreshToken
                    }
                })
                .then(({data}) => {
                    console.log("Got new tokens");
                    setTokens(data.access_token, data.refresh_token, remember);
                    requestsToRefresh.forEach((cb) => cb(currentToken));
                })
                .catch( err => {
                    // If you have a closed system, you can also
                    // redirect/router to the login page
                    clearTokens();
                    requestsToRefresh.forEach((cb) => cb(null))
                })
                .finally(() => {
                    requestsToRefresh = []
                    isRefreshing = false
                });
            };
        
            return new Promise((resolve, reject) => {
                // In our variable (requests that expect a new token
                // from the first request), we add a callback,
                // which the first request to execute
                requestsToRefresh.push((token) => {
                    if (token) {
                        config.headers.Authorization = 'Bearer ' + token;
                        resolve(axios(config));
                    };
                    // If the first request could not update the token, we
                    // must return the basic request processing logic
                    reject(err);
                });
            });
        };
    };
    return Promise.reject(err);

});


export const loginRequest = (username, password, failureHandler, rememberMe) => {
    const loginJson = {username, password};
    // send the username and password to the server
   
    return axios({method: "POST", url:Endpoints.LOGIN, data: loginJson})
    .then(response => {
        if(response.status === 200){
            const {access_token, refresh_token} = response.data.tokens;
            setTokens(access_token, refresh_token, rememberMe);
            
            return {
                user: response.data.user,
                error: ""
            }
        };
    })
    .catch( err => {
        if (err?.response?.data){
            return {
                user: null,
                error: err.response.data.message
            }
        }
        failureHandler(true)
    });
};

export const passcodeRequest = (passcode) => {
    return axios({method: "POST", url: Endpoints.PASSCODE, data: passcode})
    .then(response => {
        if(response.status === 200){
            const {access_token, refresh_token} = response.data.tokens;
            setTokens(access_token, refresh_token, true);
            
            return {
                user: response.data.user,
                error: ""
            }
        };
    })
    .catch(err => {
        return {
            user:null,
            error: "Invalid passcode"
        }
    })
}


export const updateResourceRequest = (endpoint, body) => {
    return authenticatedRequest("PUT", endpoint, body)
    .then (res => {
        if(res.status === 200){
            return { response: res.data, error: ""};
        }
        return { response: "",  error: res.error};
    })
}

export const lastSolutionsRequest = (id, profile) => {
    return authenticatedApiClient({method: "POST", url: Endpoints.USER_LAST_SOLUTIONS.replace("{id}", id).replace("{profile}", profile)})
    .then(res => {
        return {
            res
        };
    })
    .catch( error => {
        return {user: null, error: "The service is temporarily unavailable, try again later", status: 503};
    });
}

export const userInfoFromJwtRequest = (token , refreshToken) => {
    setTokens(token, refreshToken, true);

    return authenticatedApiClient({method: "GET", url: Endpoints.USER_INFO})
    .then(res => {
        if (res.status === 200){
            return {
                user: res.data,
                error: "",
                status: 200
            };
        };
    })
    .catch( error => {
        if(error.response){
            if(error.response.status === 401){
                return {user: null, error: "Your token has expired. Please Sign In again", status: 401};
            }
        };
        return {user: null, error: "The service is temporarily unavailable, try again later", status: 503};
    });
};

export const solutionGroupsRequest = () => {
    return authenticatedApiClient({method: "GET", url: Endpoints.SOLUTIONS_GROUPS})
    .then( res => {
        if (res.status === 200) {
            return {data: res.data, error: "", headers: res.headers};
        }
        return {data: null, error: res.error};
    })
    .catch( error => {
        if(error.response){
            if(error.response.status === 401){
                return {user: null, error: "Your token has expired. Please Sign In again", status: 401};
            }
        };
        return {user: null, error: "The service is temporarily unavailable, try again later", status: 503};
    });
}

export const customViewsRequest = (userId, scope, p, size) => {
    const params = {userId, scope, p, size};
    return authenticatedApiClient({method: "GET", url: Endpoints.CUSTOM_VIEWS, params})
    .then( res => {
        if (res.status === 200) {
            return {data: res.data, error: "", headers: res.headers};
        }
        return {data: null, error: res.error};
    })
    .catch( error => {
        if(error.response){
            if(error.response.status === 401){
                return {user: null, error: "Your token has expired. Please Sign In again", status: 401};
            }
        };
        return {user: null, error: "The service is temporarily unavailable, try again later", status: 503};
    });
}

export const customViewRequest = (userId, viewId) => {
    const params = {userId};
    return authenticatedApiClient({method: "POST", url: Endpoints.CUSTOM_VIEWS + `/${viewId}`, params})
    .then( res => {
        if (res.status === 200) {
            return {data: res.data, error: "", headers: res.headers};
        }
        return {data: null, error: res.error};
    })
    .catch( error => {
        if(error.response){
            if(error.response.status === 401){
                return {user: null, error: "Your token has expired. Please Sign In again", status: 401};
            }
        };
        return {user: null, error: "The service is temporarily unavailable, try again later", status: 503};
    });
}

export const createCustomViewRequest = (name, scope, configValues, userId, messageFunction) => {
    const params = {userId};

    const values = JSON.stringify(configValues);

    const data = {name, scope, "configValues": values};

    return authenticatedApiClient({method: "POST", url: Endpoints.NEW_CUSTOM_VIEW, params, data, headers: { "Content-Type": "application/json" }})
    .then( res => {
        if (res.status === 200) {
            messageFunction({text: "Solution configuration created successfully", status: true});
            return {data: res.data, error: "", headers: res.headers};
        }
        return {data: null, error: res.error};
    })
    .catch( error => {
        if(error.response){
            if(error.response.status === 401){
                messageFunction({text: "Your token has expired. Please Sign In again", status: false});
                return {user: null, error: "Your token has expired. Please Sign In again", status: 401};
            }
        };
        console.log(error)
        messageFunction({text: "The service is temporarily unavailable, try again later", status: false});
        return {user: null, error: "The service is temporarily unavailable, try again later", status: 503};
    });
}

export const updateCustomViewRequest = (name, scope, configValues, userId, viewId, messageFunction) => {
    const params = {userId};
    const data = {name, scope, "configValues": JSON.stringify(configValues)};
    return authenticatedApiClient({method: "PUT", url: Endpoints.CUSTOM_VIEWS + `/${viewId}`, params, data, headers: { "Content-Type": "application/json" }})
    .then( res => {
        if (res.status === 200) {
            messageFunction({text: "Solution configuration updated successfully", status: true});
            return {data: res.data, error: "", headers: res.headers};
        }
        return {data: null, error: res.error};
    })
    .catch( error => {
        if(error.response){
            if(error.response.status === 401){
                messageFunction({text: "Your token has expired. Please Sign In again", status: false});
                return {user: null, error: "Your token has expired. Please Sign In again", status: 401};
            }
        };
        messageFunction({text: "The service is temporarily unavailable, try again later", status: false});
        return {user: null, error: "The service is temporarily unavailable, try again later", status: 503};
    });
}

export const deleteCustomViewRequest = (userId, viewId, messageFunction) => {
    const params = {userId};
    return authenticatedApiClient({method: "DELETE", url: Endpoints.CUSTOM_VIEWS + `/${viewId}`, params})
    .then( res => {
        if (res.status === 200) {
            messageFunction({text: "Solution configuration deleted successfully", status: true});
            return {data: res.data, error: "", headers: res.headers};
        }
        return {data: null, error: res.error};
    })
    .catch( error => {
        if(error.response){
            if(error.response.status === 401){
                messageFunction({text: "Your token has expired. Please Sign In again", status: false});
                return {user: null, error: "Your token has expired. Please Sign In again", status: 401};
            }
        };
        messageFunction({text: "The service is temporarily unavailable, try again later", status: false});
        return {user: null, error: "The service is temporarily unavailable, try again later", status: 503};
    });
}

export const cloneCustomViewRequest = (userId, viewId, messageFunction) => {
    const params = {userId};
    return authenticatedApiClient({method: "POST", url: Endpoints.CUSTOM_VIEWS + `/${viewId}/clone`, params})
    .then( res => {
        if (res.status === 200) {
            messageFunction({text: "Solution configuration cloned successfully", status: true});
            return {data: res.data, error: "", headers: res.headers};
        }
        return {data: null, error: res.error};
    })
    .catch( error => {
        if(error.response){
            if(error.response.status === 401){
                messageFunction({text: "Your token has expired. Please Sign In again", status: false});
                return {user: null, error: "Your token has expired. Please Sign In again", status: 401};
            }
        };
        messageFunction({text: "The service is temporarily unavailable, try again later", status: false});
        return {user: null, error: "The service is temporarily unavailable, try again later", status: 503};
    });
}

export const logginRequest = (userId, profileId, solutionId) => {
    if(solutionId) {
        profileId = profileId ? profileId : -1;
        const body = {userId, solutionId, profileId};
        authenticatedRequest("POST", Endpoints.LOGGING, body)
        .then( res => {
            if(res.data)
                console.log(res.data);
        })
        .catch (err => {
            console.log("Failed to log user activity with error: " + err);
        });
    }
};

export const buildFinalUrl = (endpoint, facets = null, queries = null) => {
    if (facets && facets.length)
        for(let facet of facets)
            endpoint += `/${facet}`;
    
    if(queries){
        endpoint += "?"
        Object.entries(queries).forEach( q => {
            endpoint += `${q[0]}=${q[1]}&`
        });
    }
    
    return endpoint;
}

export const dataRequest  = (endpoint, datasource, filter, profileId, solutionId, onSuccess, onError, disableRls, format = null, dataReverse) => {
    if(endpoint && datasource && profileId && solutionId && onSuccess && onError) {

        let updatedOnSuccess = onSuccess;

        if(format === "BAR_CHART"){
            updatedOnSuccess = (data) => {
                const datasets = [];

                let series = data.series;
                let labels = data.labels;

                if(dataReverse){
                    series = series.slice().reverse();
                    labels = labels.slice().reverse();
                }

                
                series.forEach(d => {
                        datasets.push({
                            data: d,
                            borderColor: "#C75FF5",
                            backgroundColor: "#C75FF5",
                            borderWidth: 1,
                            barThickness: 20,
                            maxBarThickness: 20,
                            borderRadius: 0,
                            borderSkipped: false
                        })
                });
                const content = {labels, totals: data.totals, datasets}
                onSuccess(content);
            };
        }else if(format === "LINE_CHART"){
            updatedOnSuccess = (data) => {
                const datasets = [];

                let series = data.series;
                let labels = data.labels;

                if(dataReverse){
                    series = series.slice().reverse();
                    labels = labels.slice().reverse();
                }

                series.forEach((d, x) => {
                    datasets.push({
                        data: d,
                        borderColor:  x ? "#F6680B" : "#C75FF5",
                        backgroundColor: "rgba(250, 250, 250, 0)",
                        pointBackgroundColor: "rgba(250, 250, 250, 0)",
                        pointBorderColor: "rgba(250, 250, 250, 0)",
                        shadowColor:  x ? "#F6680B" : "#C75FF5",
                        shadowBlur: 1,
                        shadowOffsetX: 2,
                        shadowOffsetY: 2,
                        fill: true,
                        tension: 0,
                        pointRadius: 6,
                        pointHoverRadius: 10 ,
                        label: formatYearMonth(data.labels[0], data.labels[data.labels.length - 1], x) 
                    })
                });
                const content = {labels, maxValue: Math.max(...data.series[0]), datasets}
                onSuccess(content);
            }
        }else if(format === "TARGET_BAR_CHART"){
            updatedOnSuccess = (data) => {
                
                const datasets = [];

                let series = data.series.barsData[Object.keys(data.series.barsData)[0]];
                let targets =  data.series.dotsData[Object.keys(data.series.dotsData)[0]];
                let labels = data.labels;

                if(dataReverse){
                    series = series.slice().reverse();
                    targets = targets.slice().reverse();
                    labels = labels.slice().reverse();
                }
                datasets.push({
                    type: "scatter",
                    data: targets.length > 0 ? targets : [...labels.map( () => null)],
                    pointBackgroundColor: "#F6680B",
                    pointBorderColor: "#F6680B",
                    fill: true,
                    pointRadius: 13,
                    pointHoverRadius: 14,
                    label: "" 
                })
                
                datasets.push({
                    type: "bar",
                    data: series,
                    borderColor: "#C75FF5",
                    backgroundColor: "#C75FF5",
                    fill: true,
                    borderWidth: 0,
                    barThickness: 20,
                    maxBarThickness: 20,
                    borderRadius: 0,
                    label: "" ,
                    borderSkipped: false
                })

                const content = {labels, datasets}
                onSuccess(content);
            }
        }
        const body = {datasource, filter, profileId, solutionId, format, disableRls};
        authenticatedPostRequest(endpoint, body, updatedOnSuccess, onError);
    };
};


export const authenticatedPostRequest = (url, body, onSuccess, onError) => {
    authenticatedRequest("POST", url, body)
    .then ( res => {
        if(res.status === 200){
            onSuccess(res.data);
            onError("");
        }
        else {
            onError(res.error);
        }
    })
    .catch( err => {
        onError(err.response);
    });
};

const authenticatedRequest = (method, url, body) => {
    let config = {method: method, url: url};
    if(method === "POST" || method === "PUT"){
        config["data"] = body;
    }
    return authenticatedApiClient(config)
    .then( res => {
        return {data: res.data, error: "", status: res.status};
    })
    .catch( err => {
        if(err.response)
            return {data: null, error: err.response.data, status: err.response.status};
        return {data: null, error: "service unavailable", status: 503};
    });
};


export const postRequest = (endpoint, body) => {
    return axios({method: "POST", url: endpoint, data: body})
    .then(response => {
        if(response.status === 200)
            return {
                ok: true,
                error: ""
            }
    })
    .catch( err => {
        if (err.response){
            return {
                ok: false,
                error: err.response.data
            }
        }
        return {
            ok:false,
            error: "The service is temporarily unavailable, try again later"
        }
    });
};
